import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CodeIcon from "@mui/icons-material/Code";
import ExplorerSidebar from "./ExplorerSidebar";
import JsonResponseViewer from "./JsonResponseViewer";
import { graphqlRequestRaw, getGraphqlUrl } from "../../common/apiClient";
import { buildGraphqlCurl } from "../../utils/graphqlExportCurl";
import {
  buildOperationQuery,
  formatGraphqlQuery,
  parseGraphqlSchema,
  getBundledSchemaSdl,
  isValidGraphqlSdl,
} from "../../utils/graphqlSchemaParser";
import "./GraphqlExplorer.css";

const HISTORY_KEY = "tagnize_graphql_history";
const MAX_HISTORY = 20;
const DEFAULT_RESPONSE_RATIO = 0.38;

const DEFAULT_QUERIES = {
  listDocuments: `query ListDocuments {
  listDocuments(limit: 5) {
    totalCount
    items {
      document_id
      filename
      status
      doc_type
      updated_at
    }
  }
}`,
};

function buildDefaultQuery(op) {
  if (DEFAULT_QUERIES[op.name]) return DEFAULT_QUERIES[op.name];
  if (op.kind === "MUTATION") {
    return `mutation ${op.name} {\n  ${op.name} {\n    # completar parámetros\n  }\n}`;
  }
  return `query ${op.name} {\n  ${op.name} {\n    # completar campos\n  }\n}`;
}

function loadHistory() {
  try {
    return JSON.parse(sessionStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveHistory(entry) {
  const prev = loadHistory().filter((h) => h.query !== entry.query);
  sessionStorage.setItem(HISTORY_KEY, JSON.stringify([entry, ...prev].slice(0, MAX_HISTORY)));
}

export default function GraphqlExplorer({ operations, tokens = [], sdl = "", sdlSource = null }) {
  const [query, setQuery] = useState(DEFAULT_QUERIES.listDocuments);
  const [variables, setVariables] = useState("{\n  \n}");
  const [selectedOp, setSelectedOp] = useState("listDocuments");
  const [authMode, setAuthMode] = useState("session");
  const [patToken, setPatToken] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("operations");
  const [editorTab, setEditorTab] = useState("query");
  const [history, setHistory] = useState(loadHistory);
  const [responseRatio, setResponseRatio] = useState(DEFAULT_RESPONSE_RATIO);
  const [snack, setSnack] = useState("");
  const [responseView, setResponseView] = useState("tree");

  const editorRef = useRef(null);
  const draggingRef = useRef(false);
  const workspaceRef = useRef(null);

  const effectiveSdl = isValidGraphqlSdl(sdl) ? sdl : getBundledSchemaSdl();
  const schemaTree = useMemo(() => parseGraphqlSchema(effectiveSdl), [effectiveSdl]);

  const tokenOptions = useMemo(
    () => [
      { id: "session", label: "Sesión actual" },
      ...tokens.map((t) => ({ id: t.id, label: `${t.name} (${t.prefix}…)` })),
    ],
    [tokens]
  );

  const resolveToken = useCallback(async () => {
    if (authMode === "session") return null;
    if (authMode === "pat-manual") return patToken.trim() || null;
    return sessionStorage.getItem(`tagnize_pat_${authMode}`) || null;
  }, [authMode, patToken]);

  const handleSelectOp = (op) => {
    setSelectedOp(op.name);
    setQuery(buildDefaultQuery(op));
    setEditorTab("query");
  };

  const handleSelectSchemaOperation = (field, kind) => {
    setSelectedOp(field.name);
    setQuery(buildOperationQuery(field, kind, schemaTree));
    setEditorTab("query");
  };

  const handleInsertField = (fieldName) => {
    const el = editorRef.current;
    if (!el) {
      setQuery((q) => `${q}\n    ${fieldName}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const insertion = fieldName;
    const next = query.slice(0, start) + insertion + query.slice(end);
    setQuery(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + insertion.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSelectHistory = (entry) => {
    setQuery(entry.query);
    setVariables(entry.variables || "{}");
    setEditorTab("query");
  };

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      let parsedVars = {};
      if (variables.trim()) parsedVars = JSON.parse(variables);
      const tokenOverride = await resolveToken();
      const response = await graphqlRequestRaw(query, parsedVars, tokenOverride);
      setResult(response);
      const entry = { query, variables, at: new Date().toISOString() };
      saveHistory(entry);
      setHistory(loadHistory());
    } catch (err) {
      setError(err.message || String(err));
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const handleFormat = () => {
    try {
      setQuery(formatGraphqlQuery(query));
      setSnack("Query formateada");
    } catch {
      setSnack("No se pudo formatear — revisá la sintaxis");
    }
  };

  const handleCopyCurl = async () => {
    const tokenOverride = await resolveToken();
    const { getAccessToken } = await import("../../common/apiClient");
    const token = tokenOverride || (await getAccessToken());
    let parsedVars = {};
    try {
      parsedVars = variables.trim() ? JSON.parse(variables) : {};
    } catch {
      parsedVars = {};
    }
    await navigator.clipboard.writeText(
      buildGraphqlCurl({ url: getGraphqlUrl(), query, variables: parsedVars, token })
    );
    setSnack("cURL copiado al portapapeles");
  };

  const handleCopyResponse = async () => {
    if (!result?.body) return;
    await navigator.clipboard.writeText(JSON.stringify(result.body, null, 2));
    setSnack("Respuesta copiada");
  };

  const onResizerMouseDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !workspaceRef.current) return;
      const rect = workspaceRef.current.getBoundingClientRect();
      const offset = e.clientY - rect.top;
      const ratio = 1 - offset / rect.height;
      setResponseRatio(Math.min(0.65, Math.max(0.2, ratio)));
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  useEffect(() => {
    if (operations?.length && !operations.find((o) => o.name === selectedOp)) {
      setSelectedOp(operations[0]?.name);
    }
  }, [operations, selectedOp]);

  const responseData = result?.body?.data ?? null;
  const responseErrors = result?.body?.errors ?? (error ? [error] : null);
  const statusOk = result && result.status >= 200 && result.status < 300 && !responseErrors?.length;

  return (
    <div className="gql-explorer">
      <div className="gql-toolbar">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Autenticación</InputLabel>
          <Select label="Autenticación" value={authMode} onChange={(e) => setAuthMode(e.target.value)}>
            {tokenOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
            ))}
            <MenuItem value="pat-manual">PAT manual</MenuItem>
          </Select>
        </FormControl>
        {authMode === "pat-manual" && (
          <TextField
            size="small"
            label="Bearer token"
            value={patToken}
            onChange={(e) => setPatToken(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
            placeholder="tgnz_dev_…"
          />
        )}
        <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={handleRun} disabled={running}>
          {running ? "Ejecutando…" : "Ejecutar"}
        </Button>
        <Button variant="outlined" startIcon={<CodeIcon />} onClick={handleFormat}>
          Formatear
        </Button>
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyCurl}>
          Copiar cURL
        </Button>
      </div>

      {authMode !== "session" && (
        <Alert severity="info" sx={{ borderRadius: 0, py: 0.25 }}>
          Los PAT requieren authorizer extendido. Si recibís 401, usá sesión actual.
        </Alert>
      )}

      <div className="gql-workspace">
        <ExplorerSidebar
          sidebarTab={sidebarTab}
          onSidebarTabChange={setSidebarTab}
          operations={operations}
          selectedOp={selectedOp}
          onSelectOp={handleSelectOp}
          schemaTree={schemaTree}
          onSelectSchemaOperation={handleSelectSchemaOperation}
          onInsertField={handleInsertField}
          history={history}
          onSelectHistory={handleSelectHistory}
          sdlSource={sdlSource}
        />

        <div className="gql-main" ref={workspaceRef}>
          <div
            className="gql-editor-section"
            style={{ flex: `${1 - responseRatio} 1 0` }}
          >
            <div className="gql-panel-tabs">
              {[
                { id: "query", label: "Query" },
                { id: "variables", label: "Variables" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`gql-panel-tab ${editorTab === tab.id ? "active" : ""}`}
                  onClick={() => setEditorTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {editorTab === "query" ? (
              <div className="gql-editor-wrap">
                <textarea
                  ref={editorRef}
                  className="gql-editor"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  spellCheck={false}
                  aria-label="GraphQL query editor"
                />
              </div>
            ) : (
              <div className="gql-variables-wrap">
                <textarea
                  className="gql-variables"
                  value={variables}
                  onChange={(e) => setVariables(e.target.value)}
                  spellCheck={false}
                  aria-label="GraphQL variables JSON"
                />
              </div>
            )}
          </div>

          <div
            className={`gql-resizer ${draggingRef.current ? "dragging" : ""}`}
            onMouseDown={onResizerMouseDown}
            role="separator"
            aria-orientation="horizontal"
            aria-label="Redimensionar panel de respuesta"
          />

          <div
            className="gql-response-section"
            style={{ flex: `${responseRatio} 0 0` }}
          >
            <div className="gql-response-header">
              <span className="gql-response-title">Response</span>
              {result && (
                <div className="gql-response-meta">
                  <span className={statusOk ? "gql-status-ok" : "gql-status-error"}>
                    {result.status} {statusOk ? "OK" : "Error"}
                  </span>
                  <span>{result.elapsed} ms</span>
                  <span>{result.tokenUsed === "pat" ? "PAT" : "Sesión"}</span>
                </div>
              )}
              <div className="gql-response-actions">
                <button type="button" onClick={() => setResponseView("tree")} className={responseView === "tree" ? "active" : ""}>
                  Árbol
                </button>
                <button type="button" onClick={() => setResponseView("raw")} className={responseView === "raw" ? "active" : ""}>
                  JSON
                </button>
                <button type="button" onClick={handleCopyResponse} disabled={!result}>
                  Copiar
                </button>
              </div>
            </div>
            <div className="gql-response-body">
              {responseView === "tree" ? (
                <JsonResponseViewer data={responseData} errors={responseErrors} />
              ) : result ? (
                <pre style={{ margin: 0, fontSize: 12, fontFamily: "inherit" }}>
                  {JSON.stringify(result.body, null, 2)}
                </pre>
              ) : (
                <JsonResponseViewer data={null} errors={error ? [error] : null} />
              )}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={2500}
        message={snack}
        onClose={() => setSnack("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
}
